import React, { useState } from 'react';
import { Box, Container, Typography, Card, CardContent, Avatar, IconButton, useTheme, Grid, Button } from '@mui/material';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import FormatQuoteIcon from '@mui/icons-material/FormatQuote';
import SectionHeader from '../SectionHeader';
import StoreButtons from '../StoreButtons';

const TestimonialsSection = () => {
  const theme = useTheme();
  const [activeIndex, setActiveIndex] = useState(0);
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  const testimonials = [
    {
      name: 'Александр Петров',
      position: 'Главный врач, Медицинский центр "Здоровье"',
      image: '/images/doctor1.jpg', // Placeholder, you'll need to add these images
      quote: 'ДокМедЧат значительно улучшил коммуникацию между врачами и пациентами. Врачи освободили до 30% рабочего времени, а трекер задач повысил качество ухода за пациентами.',
    },
    {
      name: 'Елена Смирнова',
      position: 'Администратор клиники, Центр семейной медицины',
      image: '/images/admin1.jpg',
      quote: 'Возможность создавать групповые чаты без раскрытия личных данных врачей - прекрасное решение. Особенно ценно хранение данных на нашем собственном сервере.',
    },
    {
      name: 'Дмитрий Иванов',
      position: 'Кардиолог, Кардиологический центр',
      image: '/images/doctor2.jpg',
      quote: 'Ценю возможность отвечать пациентам в удобное время. Это позволяет лучше планировать день и уделять больше внимания каждому пациенту.',
    },
    {
      name: 'Наталья Козлова',
      position: 'Директор по ИТ, Многопрофильная клиника',
      image: '/images/it-director.jpg',
      quote: 'С точки зрения ИТ-безопасности, ДокМедЧат превзошел ожидания. Размещение сервера в нашей инфраструктуре и надежное шифрование делают систему отличным выбором.',
    },
  ];

  const handleNext = () => {
    setActiveIndex((prevIndex) => (prevIndex + 1) % testimonials.length);
  };

  const handlePrev = () => {
    setActiveIndex((prevIndex) => (prevIndex - 1 + testimonials.length) % testimonials.length);
  };

  const getVisibleTestimonials = () => {
    // For desktop, show 2 testimonials at a time
    return [
      testimonials[activeIndex],
      testimonials[(activeIndex + 1) % testimonials.length],
    ];
  };

  return (
    <Box
      id="testimonials"
      sx={{
        py: 10,
        backgroundColor: 'rgba(46, 80, 119, 0.05)',
      }}
      ref={ref}
    >
      <Container maxWidth="lg">
        <SectionHeader
          title="Отзывы клиентов"
          subtitle="Узнайте, что говорят о ДокМедЧат медицинские специалисты и администраторы клиник."
        />

        <Box sx={{ position: 'relative', mt: 5 }}>
          <Box sx={{ display: { xs: 'none', md: 'flex' }, justifyContent: 'space-between' }}>
            {getVisibleTestimonials().map((testimonial, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
                transition={{ duration: 0.5, delay: index * 0.2 }}
                style={{ width: '48%' }}
              >
                <Card
                  sx={{
                    height: '100%',
                    borderRadius: 3,
                    boxShadow: '0 10px 30px rgba(0, 0, 0, 0.07)',
                    position: 'relative',
                    overflow: 'visible',
                    transition: 'transform 0.3s ease-in-out',
                    '&:hover': {
                      transform: 'translateY(-10px)',
                    },
                  }}
                >
                  <CardContent sx={{ p: 4 }}>
                    <FormatQuoteIcon
                      sx={{
                        position: 'absolute',
                        top: -15,
                        left: 20,
                        fontSize: 40,
                        color: theme.palette.primary.main,
                        bgcolor: 'white',
                        borderRadius: '50%',
                        p: 1,
                        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
                      }}
                    />
                    <Typography variant="body1" paragraph sx={{ mt: 3, mb: 3, lineHeight: 1.7 }}>
                      "{testimonial.quote}"
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', mt: 4 }}>
                      <Avatar
                        src={testimonial.image}
                        sx={{
                          width: 56,
                          height: 56,
                          mr: 2,
                          border: '2px solid',
                          borderColor: 'primary.light',
                        }}
                      />
                      <Box>
                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                          {testimonial.name}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {testimonial.position}
                        </Typography>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </Box>

          {/* Mobile view - single testimonial */}
          <Box sx={{ display: { xs: 'block', md: 'none' } }}>
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
              transition={{ duration: 0.5 }}
            >
              <Card
                sx={{
                  borderRadius: 3,
                  boxShadow: '0 10px 30px rgba(0, 0, 0, 0.07)',
                  position: 'relative',
                  overflow: 'visible',
                }}
              >
                <CardContent sx={{ p: 4 }}>
                  <FormatQuoteIcon
                    sx={{
                      position: 'absolute',
                      top: -15,
                      left: 20,
                      fontSize: 40,
                      color: theme.palette.primary.main,
                      bgcolor: 'white',
                      borderRadius: '50%',
                      p: 1,
                      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
                    }}
                  />
                  <Typography variant="body1" paragraph sx={{ mt: 3, mb: 3, lineHeight: 1.7 }}>
                    "{testimonials[activeIndex].quote}"
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', mt: 4 }}>
                    <Avatar
                      src={testimonials[activeIndex].image}
                      sx={{
                        width: 56,
                        height: 56,
                        mr: 2,
                        border: '2px solid',
                        borderColor: 'primary.light',
                      }}
                    />
                    <Box>
                      <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        {testimonials[activeIndex].name}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {testimonials[activeIndex].position}
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </motion.div>
          </Box>

          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              mt: 4,
              gap: 2,
            }}
          >
            <IconButton
              onClick={handlePrev}
              sx={{
                bgcolor: 'white',
                boxShadow: '0 2px 10px rgba(0, 0, 0, 0.1)',
                '&:hover': {
                  bgcolor: 'primary.light',
                  color: 'white',
                },
              }}
            >
              <ArrowBackIosNewIcon fontSize="small" />
            </IconButton>
            <IconButton
              onClick={handleNext}
              sx={{
                bgcolor: 'white',
                boxShadow: '0 2px 10px rgba(0, 0, 0, 0.1)',
                '&:hover': {
                  bgcolor: 'primary.light',
                  color: 'white',
                },
              }}
            >
              <ArrowForwardIosIcon fontSize="small" />
            </IconButton>
          </Box>
        </Box>

        <Grid container spacing={3} sx={{ mt: 5, justifyContent: 'center' }}>
          <Grid item xs={12} md={10}>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
              transition={{ duration: 0.6, delay: 0.3 }}
            >
              <Box
                sx={{
                  textAlign: 'center',
                  p: 4,
                  borderRadius: 3,
                  bgcolor: 'white',
                  boxShadow: '0 5px 20px rgba(0, 0, 0, 0.05)',
                }}
              >
                <Typography variant="h5" sx={{ mb: 2, color: 'primary.main', fontWeight: 600 }}>
                  Готовы повысить эффективность коммуникации в вашей клинике?
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Присоединяйтесь к растущему числу медицинских учреждений, которые доверяют ДокМедЧат для безопасной коммуникации.
                </Typography>
                <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                  <StoreButtons />
                </Box>
              </Box>
            </motion.div>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

export default TestimonialsSection; 